import { useCallback, useEffect, useMemo } from 'react';
import { RouterProvider } from 'react-router';
import { ToastsContainer, ToastVariant, type ToastData } from 'qampo-ui-library';
import i18n from './services/translations/translationService';
import { useAuth } from './hooks/useAuth';
import router from './routes/routerConfig';
import { ErrorDialog } from './components/ErrorDialog/ErrorDialog';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { selectErrorDialog, dismissError } from './store/errorDialog/errorDialogSlice';
import { selectToasts, dismissToast, type AppToast } from './store/toast/toastSlice';
import styles from './App.module.css';

// TODO: Toast/ToastsContainer only exist in qampo-ui-library's legacy root tier (no
// base-tier version yet). Add a base version of the toast to the foundation library,
// then consume it here via a src/components/UI/base wrapper like the other base components.
// Maps the store's library-agnostic toast variant onto qampo's ToastVariant enum.
const toastVariantMap: Record<AppToast['variant'], ToastVariant> = {
  notification: ToastVariant.NOTIFICATION,
  warning: ToastVariant.WARNING,
  error: ToastVariant.ERROR
};

const toAppToastData = (toast: AppToast): ToastData => ({
  toastId: toast.id,
  toastMessage: toast.message,
  toastType: toastVariantMap[toast.variant],
  toastHeader: toast.header,
  autoDismiss: toast.autoDismiss,
  position: toast.position
});

function App() {
  const { isAuthenticated } = useAuth();
  const dispatch = useAppDispatch();
  const errorDialog = useAppSelector(selectErrorDialog);
  const toasts = useAppSelector(selectToasts);

  // The library's Toast restarts its auto-dismiss timer whenever its props change
  // identity, so both the mapped data and the close handler must stay referentially
  // stable across App re-renders — otherwise every re-render resets every countdown.
  const toastData = useMemo(() => toasts.map(toAppToastData), [toasts]);
  const handleToastClose = useCallback(
    (toastId: string) => {
      dispatch(dismissToast(toastId));
    },
    [dispatch]
  );

  //TODO: Find-out belt ref or belt name from the url params
  const params = new URLSearchParams(window.location.search);
  const language = params.get('language');

  useEffect(() => {
    if (language) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  return (
    <div>
      {isAuthenticated && <RouterProvider router={router} />}
      {errorDialog.message && (
        <ErrorDialog
          title={errorDialog.title ?? undefined}
          message={errorDialog.message}
          onClose={() => dispatch(dismissError())}
        />
      )}
      {/* The library's ToastsContainer has no live-region semantics of its own, so toasts
          appear and vanish silently for screen readers. One polite region covers every
          variant; the assertive case is served by ErrorDialog's role="alert". */}
      <div aria-live="polite">
        <ToastsContainer
          classes={styles.toasts}
          toasts={toastData}
          onCloseHandler={handleToastClose}
        />
      </div>
    </div>
  );
}

export default App;
